import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LogoMark } from '@/components/Logo';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error('Invalid reset link');
      navigate('/forgot-password');
    }
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { 
        token, 
        new_password: password 
      });
      setSuccess(true);
      toast.success('Password reset successful!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to reset password. The link may have expired.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return null;
  }

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2.5 mb-8" data-testid="reset-home-link">
          <LogoMark size={40} />
          <span className="text-xl font-bold font-display tracking-tight">
            <span className="text-ink">Portfolio</span>
            <span className="text-signal-blue"> Pal</span>
          </span>
        </Link>

        <div className="bg-white rounded-2xl border border-line p-8 shadow-sm">
          {!success ? (
            <>
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-ink font-display tracking-tight">Set new password</h1>
                <p className="mt-2 text-muted-brand">
                  Enter your new password below.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="password" className="text-slate-brand">New password</Label>
                  <div className="relative mt-2">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      data-testid="reset-password-input"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 6 characters"
                      className="h-11 pr-10 rounded-xl border-line focus:border-signal-blue focus:ring-signal-blue"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      data-testid="reset-password-visibility-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-brand hover:text-ink transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="text-slate-brand">Confirm new password</Label>
                  <Input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    data-testid="reset-confirm-password-input"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    className="mt-2 h-11 rounded-xl border-line focus:border-signal-blue focus:ring-signal-blue"
                    disabled={loading}
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="reset-submit-btn"
                  className="w-full h-11 bg-anchor-blue hover:bg-anchor-blue/90 rounded-xl font-semibold"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                      Resetting...
                    </span>
                  ) : (
                    'Reset password'
                  )}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center">
              <div className="w-16 h-16 bg-meadow-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-meadow-green" />
              </div>
              <h2 className="text-xl font-bold text-ink font-display mb-2">Password reset!</h2>
              <p className="text-muted-brand mb-6">
                Your password has been successfully reset. You can now log in with your new password.
              </p>
              <Link to="/login" data-testid="reset-go-to-login-link">
                <Button className="w-full h-11 bg-anchor-blue hover:bg-anchor-blue/90 rounded-xl font-semibold">
                  Go to login
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
